import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { SubSink } from 'subsink';
import { InvoiceStateService } from './state-and-data-handling/invoice-state.service';
import { map, take, tap } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { InvoiceDocumentDetailModel } from '../../../../core/models/invoice/invoice-document-detail.model';
import { EditDocumentAreaService } from '../../../../core/services/features/document-editing/edit-document-area.service';
import { InvoiceHandlingMode, SectionType } from '../../../../features/invoice-theme/theme.types';
import { findKvRecordValue } from '../../../../shared/functions/record-functions';
import { TitleMetaKey } from '../invoice.config';

@Component({
  selector: 'app-invoice-create-edit',
  templateUrl: './invoice-create-edit.component.html',
  standalone: false,
})
export class InvoiceCreateEditComponent implements OnInit, OnDestroy {
  public mode = signal<InvoiceHandlingMode>(null);
  public type = computed(() => this.editDocumentAreaService.selected()?.type);
  public title = computed<string>(
    () => findKvRecordValue(this.invoiceStateService.meta(), TitleMetaKey) ?? '',
  );

  private readonly subs = new SubSink();

  // DI
  private readonly invoiceStateService = inject(InvoiceStateService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly editDocumentAreaService = inject(EditDocumentAreaService);

  ngOnInit(): void {
    this.activatedRoute.data
      .pipe(
        take(1),
        tap((data) =>
          // Set mode
          this.mode.set(data.mode),
        ),
        map((data) => data?.invoice),
      )
      .subscribe((invoiceOrTemplate: InvoiceDocumentDetailModel) => {
        if (!invoiceOrTemplate) {
          return;
        }

        // In edit mode we load an invoice.
        if (this.mode() === 'edit') {
          this.invoiceStateService.invoiceId.set(invoiceOrTemplate.id);
          this.invoiceStateService.invoicePositions.set(invoiceOrTemplate.positions);
        }

        // In create mode we only load template stuff.
        this.invoiceStateService.replaceAllContentAreaData(invoiceOrTemplate.contentAreas);
        this.invoiceStateService.replaceThemeData(invoiceOrTemplate.themeProps);
        this.invoiceStateService.replaceMeta(invoiceOrTemplate.meta);
      });
  }

  ngOnDestroy(): void {
    this.invoiceStateService.reset();
    this.editDocumentAreaService.set({
      type: SectionType.Undefined,
      sectionId: null,
    });
    this.subs.unsubscribe();
  }
}
